import { FC } from 'react';
import { Navigate } from 'react-router';
import AuthGuard from '@/features/auth/components/AuthGuard';
import FeatureGate from '@/features/auth/components/FeatureGate';
import ParentRoute from '@/core/components/ParentRoute';
import { Route } from '@/core/types';
import { capitalize, lowerCase, upperFirst } from 'lodash';
import { Helmet } from 'react-helmet';
import { useTypedSelector } from '@/core/redux/hooks';
import { getSetting } from '@/core/redux/selectors/settingsManagement.selectors';
import { SettingsKeys } from '@/core/enums';
import { AppWhiteLabel } from '@/features/booking/types';

const getElement = (route: Route) =>
  route.isParent ? <ParentRoute route={route} /> : route.element;

const ComposeScreen: FC<{ route: Route }> = ({ route }) => {
  const formattedPathname = capitalize(
    upperFirst(lowerCase(route.path?.split('/')?.reverse()?.[0] ?? '')),
  );
  const whiteLabelSettings = useTypedSelector((state) =>
    getSetting<AppWhiteLabel>(state, SettingsKeys.WHITELABEL_APP),
  );

  const title = whiteLabelSettings?.web?.title ?? 'Wunder mobility';
  const shouldTitleIncludePath = whiteLabelSettings?.web?.shouldTitleIncludePath ?? false;
  const metaDescription = whiteLabelSettings?.web?.description ?? 'Wunder mobility user web app.';
  const favicon = whiteLabelSettings?.web?.favicon?.icon ?? '/images/favicon-32x32.png';

  const pathName = shouldTitleIncludePath ? `- ${formattedPathname}` : '';

  const pageTitle = (
    <Helmet>
      <title>
        {title} {pathName}
      </title>
      <meta name="description" content={metaDescription} />
      <link rel="icon" type="image/png" sizes="32x32" href={favicon} />
      {whiteLabelSettings?.web?.robotsMetaTags?.map((robotMeta) => (
        <meta key={`robot-${robotMeta}`} name="robots" content={robotMeta} />
      ))}
    </Helmet>
  );

  const element = getElement(route);

  // If route is not guarded, just render the element
  if (!route.guarded)
    return (
      <>
        {pageTitle}
        {element}
      </>
    );

  // If guarded is true, the user just needs to be logged in.
  if (typeof route.guarded === 'boolean') {
    return (
      <>
        {pageTitle}
        <AuthGuard>{element}</AuthGuard>
      </>
    );
  }

  // Detailed guard — auth required, plus an optional tenant feature flag.
  // When the feature is missing, send the user back to the business-account root,
  // where ParentRoute resolves to the first available section.
  return (
    <>
      {pageTitle}
      <AuthGuard>
        <FeatureGate
          feature={route.guarded.feature}
          fallback={<Navigate to="/business-account" replace />}
        >
          {element}
        </FeatureGate>
      </AuthGuard>
    </>
  );
};

export default ComposeScreen;
